import { useEffect, useState } from "react";
import { Dimmer, Grid, Loader, Message, Table } from "semantic-ui-react";
import { formatNumberToScientific } from "smart-decimal-format";
import { useGetDrawingChanges } from "../../../../api/changeHistory";
import { useGetChangeReasonLog } from "../../../../api/changeReasonLog";
import { CommonTable } from "../../../../components/commonTable/CommonTable";
import PaginationView from "../../../../components/pagination/Pagination";
import { ROLES } from "../../../../config/permission-maps";
import {
  CALCULATED_VERSION_PREFIX,
  ProductSummaryHistoryTableCol,
  ProductSummaryTableCol
} from "../../../../config/tableColConstant";
import { getUserRolesInLocal } from "../../../../utils/cacheStorage";

const ProductSummaryHistory = ({
  productSummaryHistoryData,
  pageNumber,
  pageSize,
  setPageNumber,
  setPageSize,
  productId,
  salesUnitId,
  productIsAverage
}: any) => {
  const [selectedHistory, setSelectedHistory] = useState<any>(null);
  const [totalSummaryHistory, setTotalSummaryHistory] = useState(0);
  const localUserRole = getUserRolesInLocal();
  const checkIsUserSupplier = localUserRole?.[0] === ROLES.supplier;
  const [showAllChanges, setShowAllChanges] = useState(false);
  const MAX_VISIBLE_CHANGES = 5;

  const productSummaryHistory = productSummaryHistoryData.data;
  const productSummaryHistoryList = productSummaryHistory?.result?.productSummaryHistory || [];

  const { data: drawingChanges, isFetching: isDrawingChangesLoading } = useGetDrawingChanges({
    from: selectedHistory?.changedTime?.from,
    to: selectedHistory?.changedTime?.to,
    sourceId: salesUnitId,
    ownerId: productId
  });

  const { data: changeReason, isFetching: isChangeReasonLoading } = useGetChangeReasonLog({
    from: selectedHistory?.changedTime?.from,
    to: selectedHistory?.changedTime?.to,
    sourceId: salesUnitId,
    ownerId: productId
  });

  useEffect(() => {
    if (productSummaryHistory) {
      setPageNumber(productSummaryHistory.page);
      setPageSize(productSummaryHistory.pageSize);
      setTotalSummaryHistory(productSummaryHistory.total);
    }
  }, [productSummaryHistory]);

  const loadHistoryTableData = () => {
    return productSummaryHistoryList?.map((data: any, index: any) => {
      const { climateChange } = data;
      return (
        <Table.Row className="tbleR" key={index}>
          <Table.Cell>
            <p>
              {new Date(climateChange?.createdDate).toISOString().slice(0, 19).replace("T", " ")}
            </p>
          </Table.Cell>
          <Table.Cell>
            <p>{`${climateChange.measure?.toFixed(2)}`}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{`${CALCULATED_VERSION_PREFIX} ${data?.calculateVersion}`}</p>
          </Table.Cell>
          <Table.Cell>
            <Grid>
              <Grid.Column
                computer={16}
                tablet={16}
                mobile={16}
                key={index}
                className="alignRight addViewDetails"
              >
                <div
                  key={index}
                  className={"customButtonViewSelect customButtonViewTitle"}
                  onClick={() => setSelectedHistory(data)}
                >
                  <p className={"customButtonTextSelect"}>Details</p>
                </div>
              </Grid.Column>
            </Grid>
          </Table.Cell>
        </Table.Row>
      );
    });
  };

  const loadTableData = () => {
    return selectedHistory?.totalImpact?.map((data: any, index: any) => {
      return (
        <Table.Row className="tbleR" key={index}>
          <Table.Cell>
            <p>{data.indicator}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{formatNumberToScientific(data.measure, 5)}</p>
          </Table.Cell>
          <Table.Cell>
            <p>{data.unit}</p>
          </Table.Cell>
          <Table.Cell>
            <Grid>
              <Grid.Column
                computer={16}
                tablet={16}
                mobile={16}
                key={index}
                className="alignRight addViewDetails"
              ></Grid.Column>
            </Grid>
          </Table.Cell>
        </Table.Row>
      );
    });
  };

  if (productIsAverage) {
    return <Message>History not available for the product</Message>;
  }

  return (
    <>
      {!checkIsUserSupplier ? (
        <>
          {isDrawingChangesLoading || isChangeReasonLoading ? (
            <Dimmer active>
              <Loader content="Loading" />
            </Dimmer>
          ) : (
            <>
              <CommonTable tableHeaderData={ProductSummaryHistoryTableCol}>
                {loadHistoryTableData()}
              </CommonTable>
              <div style={{ margin: "10px 0" }}>
                <PaginationView
                  currentPageNo={pageNumber}
                  totalPages={Math.ceil(totalSummaryHistory / pageSize)}
                  pageSize={pageSize}
                  currentSelectPage={(page: number) => {
                    setPageNumber(page);
                  }}
                  changePageSize={(pageSize: number) => {
                    setPageNumber(1);
                    setPageSize(pageSize);
                  }}
                />
              </div>

              {selectedHistory?.totalImpact?.length > 0 && (
                <Grid>
                  <Grid.Column computer={16} tablet={16} mobile={16}>
                    <div className="drawingChangesBox">
                      <p className="drawingChangesBoxTitle">
                        {changeReason && `Change reason - ${changeReason}`}
                      </p>
                      <p className="drawingChangesBoxTitle">
                        {drawingChanges && drawingChanges.length > 0
                          ? "Below values were changed"
                          : "No changes were made to the mapping"}
                      </p>

                      {drawingChanges && drawingChanges?.length > 0 && (
                        <>
                          <ul className="drawingChangesList">
                            {(showAllChanges
                              ? drawingChanges
                              : drawingChanges.slice(0, MAX_VISIBLE_CHANGES)
                            ).map((change, index) => (
                              <li key={index}>{change}</li>
                            ))}
                          </ul>

                          {drawingChanges.length > MAX_VISIBLE_CHANGES && (
                            <p
                              className="drawingChangesToggleText"
                              onClick={() => setShowAllChanges(!showAllChanges)}
                            >
                              {showAllChanges ? "View less" : "View more"}
                            </p>
                          )}
                        </>
                      )}
                    </div>
                  </Grid.Column>
                  <Grid.Column
                    computer={16}
                    tablet={16}
                    mobile={16}
                    style={{ marginBottom: "20px" }}
                  >
                    <CommonTable tableHeaderData={ProductSummaryTableCol}>
                      {loadTableData()}
                    </CommonTable>
                  </Grid.Column>
                </Grid>
              )}
            </>
          )}
        </>
      ) : (
        <Message color="yellow">Calculated: Upgrade to see results</Message>
      )}
    </>
  );
};

export default ProductSummaryHistory;
